import { createContext, useContext, useEffect, useMemo } from 'react';

import { PRODUCT_CATALOG_SERVICES } from '#client/constants/catalogServices';
import { useLogError } from '#client/hooks/useLogError';
import { useInitialDataContext } from '#client/InitialDataContext';
import { findLatestOrderTaskByServiceName } from '#client/utils/orderTasks';

import useToastContext from './useToastContext';

export const OrderTaskContext = createContext();
OrderTaskContext.displayName = 'OrderTaskContext';

const useOrderTaskContext = () => {
  const context = useContext(OrderTaskContext);

  if (typeof context === 'undefined') {
    throw new Error('useOrderTaskContext must be used within a OrderTaskContext.Provider');
  }

  return context;
};

export function OrderTaskProvider({ children }) {
  const { createCommonErrorToast } = useToastContext();
  const {
    error: orderTaskError,
    errors,
    data: orderTaskData,
    loading: isLoadingOrderTask
  } = useInitialDataContext();

  useLogError(errors, orderTaskError);
  const bankingPreferredTask = useMemo(
    () =>
      findLatestOrderTaskByServiceName(
        orderTaskData?.orderTasksByBusinessEntity,
        PRODUCT_CATALOG_SERVICES.BANKING_PREFERRED
      ),
    [orderTaskData?.orderTasksByBusinessEntity]
  );

  useEffect(() => {
    if (orderTaskError) {
      createCommonErrorToast({ error: orderTaskError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTaskError]);

  const value = useMemo(
    () => ({
      isLoadingOrderTask,
      orderTasks: orderTaskData?.orderTasksByBusinessEntity,
      bankingPreferredTask,
      orderTaskError
    }),
    [
      bankingPreferredTask,
      isLoadingOrderTask,
      orderTaskData?.orderTasksByBusinessEntity,
      orderTaskError
    ]
  );

  return <OrderTaskContext.Provider value={value}>{children}</OrderTaskContext.Provider>;
}

export default useOrderTaskContext;
